/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { VisTabularDatatip } from '#PRIVATE_VisTabularDatatip';
import type { BundleType } from '#resources/nls/bundle';
import type { GanttDatatipContext, GanttProps, GanttRowData, GanttTaskData } from '../gantt.types';
import { getNormalizedStartEnd } from './dataLayoutUtils';

/**
 * Gets the default datatip renderer.
 * @param options Relevant information.
 * @returns The default datatip renderer.
 */
const getDefaultDatatipRenderer = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(options: {
  translations: BundleType;
  dateFormatter: (date: string) => string;
  isRTL: boolean;
}): NonNullable<GanttProps<K2, D1, D2>['datatip']> => {
  const { translations, dateFormatter } = options;
  const defaultDatatipRenderer = ({ data, rowData }: GanttDatatipContext<D1, D2>) => {
    if (data.accessibleLabel !== undefined) {
      return { content: data.accessibleLabel };
    }

    const { start, end } = getNormalizedStartEnd(data);
    const isMilestone = start === end;

    const tableData = [];

    if (rowData.label) {
      tableData.push({ key: translations.gantt_labelRow(), value: rowData.label });
    }
    if (!isMilestone) {
      tableData.push({ key: translations.gantt_labelStart(), value: dateFormatter(start) });
      tableData.push({ key: translations.gantt_labelEnd(), value: dateFormatter(end) });
    } else {
      tableData.push({ key: translations.gantt_labelDate(), value: dateFormatter(start) });
    }
    if (data.label) {
      tableData.push({ key: translations.gantt_labelLabel(), value: data.label });
    }

    const content = <VisTabularDatatip tableData={tableData} />;
    return { content };
  };
  return defaultDatatipRenderer;
};

/**
 * Gets the datatip renderer context.
 * @param rows The rows data.
 * @param layoutObj The task layout object.
 * @returns
 */
const getRenderContext = <
  K1,
  K2,
  D1 extends GanttRowData<K1, D1, D2>,
  D2 extends GanttTaskData<K2>
>(
  rows: GanttProps<K2, D1, D2>['rows'],
  layoutObj: { index: number; rowIndex: number }
): GanttDatatipContext<D1, D2> => {
  const { index, rowIndex } = layoutObj;
  return {
    data: rows[rowIndex].tasks[index],
    rowData: rows[rowIndex]
  };
};

export { getDefaultDatatipRenderer, getRenderContext };
